// Auto-advancing, endlessly looping carousel for the Featured Spells cards.
// Shows --per-view cards (set responsively on the track), slides one card, pauses, repeats:
// 1-2-3 → 2-3-4 → 3-4-5 … The loop is seamless because the card that slides out is moved to
// the end of the track (no clones, so every card keeps unique ids and a single live form).
// Pauses on hover, keyboard focus and hidden tabs; never autoplays for reduced-motion visitors.
class SpellCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-carousel-track]');
    this.controls = this.querySelector('[data-carousel-controls]');
    this.interval = Number(this.dataset.interval) || 3500;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.busy = false;
    this.hovered = false;
    this.focused = false;

    if (!this.track) return;

    this.addEventListener('pointerenter', () => { this.hovered = true; }, { signal });
    this.addEventListener('pointerleave', () => { this.hovered = false; }, { signal });
    this.addEventListener('focusin', () => { this.focused = true; }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.focused = false;
    }, { signal });
    // Previous / next buttons target this element with commandfor + command="--previous" | "--next".
    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#next();
      if (event.command === '--previous') this.#previous();
      this.#start();
    }, { signal });
    window.addEventListener('resize', () => this.#refresh(), { signal });
    this.reducedMotion.addEventListener('change', () => this.#start(), { signal });

    this.#refresh();
    this.#start();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.#stop();
  }

  get #items() {
    return Array.from(this.track.children);
  }

  get #perView() {
    return Number(getComputedStyle(this.track).getPropertyValue('--per-view')) || 1;
  }

  get #canSlide() {
    return this.#items.length > this.#perView;
  }

  get #paused() {
    return this.hovered || this.focused || document.hidden;
  }

  // Distance between two neighbouring cards (negative in right-to-left layouts, which flips the slide).
  #stepSize() {
    const [first, second] = this.#items;
    return second.offsetLeft - first.offsetLeft;
  }

  #start() {
    this.#stop();
    if (this.reducedMotion.matches || !this.#canSlide) return;
    this.timer = window.setInterval(() => {
      if (!this.#paused) this.#next();
    }, this.interval);
  }

  #stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  // Off-screen cards are inert so keyboard focus never lands on (and scrolls to) a hidden card.
  #refresh() {
    const perView = this.#perView;
    const canSlide = this.#canSlide;
    this.#items.forEach((item, index) => {
      item.inert = canSlide && index >= perView;
    });
    if (this.controls) this.controls.hidden = !canSlide;
  }

  #slideTo(offset, done) {
    const finish = () => {
      this.track.style.transition = 'none';
      done?.();
      this.track.style.transform = '';
      void this.track.offsetWidth;
      this.track.style.transition = '';
      this.busy = false;
      this.#refresh();
    };

    if (this.reducedMotion.matches) {
      finish();
      return;
    }

    this.track.style.transition = 'transform 700ms var(--ease-ui, ease)';
    this.track.style.transform = `translateX(${offset}px)`;
    const fallback = window.setTimeout(finish, 900);
    this.track.addEventListener('transitionend', () => {
      window.clearTimeout(fallback);
      finish();
    }, { once: true });
  }

  #next() {
    if (this.busy || !this.#canSlide) return;
    this.busy = true;
    this.#slideTo(-this.#stepSize(), () => this.track.append(this.#items[0]));
  }

  #previous() {
    if (this.busy || !this.#canSlide) return;
    this.busy = true;
    const step = this.#stepSize();
    // Bring the last card to the front, start shifted by one card, then glide back into place.
    this.track.style.transition = 'none';
    this.track.prepend(this.#items.at(-1));
    this.track.style.transform = `translateX(${-step}px)`;
    void this.track.offsetWidth;
    this.#items[0].inert = false;
    this.#slideTo(0);
  }
}

if (!customElements.get('spell-carousel')) customElements.define('spell-carousel', SpellCarousel);
